import { inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { ApiShivaService } from '@core/apis/api-shiva.service';
import { BulkQuantityModalComponent } from '@core/components/bulk-quantity-modal/bulk-quantity-modal.component';
import { BulkScanResultModalComponent } from '@core/components/bulk-scan-result-modal/bulk-scan-result-modal.component';
import { ScannedEquipmentManager } from '@core/components/scanned-equipment-list/scanned-equipment.manager';
import { EquipmentLocationEnum } from '@core/constants';
import {
  IBulkQuantityModalConfig,
  IBulkQuantitySummaryRow,
  IBulkScanCandidate,
  IBulkScanOptions,
  IBulkScanRejection,
  IEquipment,
  IEquipmentModel,
  IListResult,
  IMetadataScannedEquipment,
  IRequiredEquipment,
  IWorkOrderItems,
  ScanStatusEnum,
} from '@core/interfaces';

const VALID_SCAN_STATUSES: ScanStatusEnum[] = [ScanStatusEnum.Success, ScanStatusEnum.Warning, ScanStatusEnum.Error];
const COUNTED_SCAN_STATUSES: ScanStatusEnum[] = [ScanStatusEnum.Success, ScanStatusEnum.Warning];

@Injectable({
  providedIn: 'root',
})
export class BulkEquipmentScanService {
  private readonly apiShiva = inject(ApiShivaService);
  private readonly ngbModal = inject(NgbModal);
  private readonly toastr = inject(ToastrService);
  private readonly scannedEquipmentManager = inject(ScannedEquipmentManager);

  /**
   * Run the whole bulk flow: resolve the requested SNIs, ask the quantity, validate, hand over the
   * accepted equipments to the caller and report what was rejected.
   */
  public async run(options: IBulkScanOptions): Promise<void> {
    const requested: string[] = this.getRequestedInternalSerialNumbers(options.woi);
    if (!requested.length) {
      this.toastr.warning('Aucun numéro de série interne demandé sur cette tâche.', 'Ajout par lot');
      return;
    }

    let candidates: IBulkScanCandidate[];
    try {
      candidates = await this._resolveCandidates(requested);
    } catch (error: unknown) {
      console.error(error);
      this.toastr.error('Erreur lors de la recherche des équipements. Veuillez réessayer.', 'Ajout par lot');
      return;
    }

    // Equipments already on the WOI (whatever their status) can't be picked again,
    // but only the successful / warning ones count toward the expected quantity (see `checkScanStatus`).
    const scannedCodes: string[] = this._getScannedCodes(options.woi, VALID_SCAN_STATUSES);
    const countedScannedCount: number = this._getScannedCodes(options.woi, COUNTED_SCAN_STATUSES).length;
    const pending: IBulkScanCandidate[] = this.getPendingCandidates(candidates, scannedCodes);
    const remaining: number = Math.max(options.quantityToScan - countedScannedCount, 0);
    const maxQuantity: number = Math.min(pending.length, remaining);

    if (maxQuantity <= 0) {
      this.toastr.info('Tous les équipements demandés ont déjà été scannés.', 'Ajout par lot');
      return;
    }

    const quantity: number | undefined = await this._askQuantity(options, maxQuantity, countedScannedCount, pending.length);
    if (!quantity) {
      return;
    }

    const { accepted, rejected } = this.validateCandidates(pending.slice(0, quantity), options);

    if (accepted.length) {
      options.addEquipments(accepted);
    }
    this._report(accepted.length, rejected);
  }

  /**
   * The requested internal serial numbers of the WOI, without blanks nor duplicates (case-insensitive),
   * in their requested order.
   */
  public getRequestedInternalSerialNumbers(woi: IWorkOrderItems | undefined): string[] {
    const requested: IRequiredEquipment[] = woi?.metadata?.requested_equipments || [];
    const seen: Set<string> = new Set<string>();
    const result: string[] = [];
    requested.forEach((requirement: IRequiredEquipment) => {
      const sni: string = (requirement.internal_serial_number || '').trim();
      if (sni && !seen.has(sni.toLowerCase())) {
        seen.add(sni.toLowerCase());
        result.push(sni);
      }
    });
    return result;
  }

  public removeLastScanned(equipments: IMetadataScannedEquipment[], quantity: number): IMetadataScannedEquipment[] {
    const scannedIndexes: number[] = equipments
      .map((item: IMetadataScannedEquipment, index: number) => COUNTED_SCAN_STATUSES.includes(item.scan_status as ScanStatusEnum) ? index : -1)
      .filter((index: number) => index >= 0);
    const indexesToRemove: Set<number> = new Set<number>(quantity > 0 ? scannedIndexes.slice(-quantity) : []);
    return equipments.filter((_, index: number) => !indexesToRemove.has(index));
  }

  public async runRemove(
    woi: IWorkOrderItems,
    quantityToScan: number,
    removeEquipments: (remainingEquipments: IMetadataScannedEquipment[]
    ) => void): Promise<void> {
    const scannedCount: number = this._getScannedCodes(woi, COUNTED_SCAN_STATUSES).length;
    if (scannedCount <= 0) {
      this.toastr.info('Aucun équipement scanné à retirer.', 'Retrait par lot');
      return;
    }

    const quantity: number | undefined = await this._openQuantityModal({
      title: 'Retrait par lot',
      titleIcon: 'fa-minus',
      subtitle: woi.code,
      quantityLabel: 'Quantité à retirer',
      maxQuantity: scannedCount,
      summaryRows: [
        { label: 'Quantité demandée', value: quantityToScan },
        { label: 'Quantité scannée', value: scannedCount },
      ],
      confirmButtonClass: 'btn-danger',
    });
    if (!quantity) {
      return;
    }

    removeEquipments(this.removeLastScanned(woi.metadata?.equipments || [], quantity));
    this.toastr.success(`${quantity} équipement(s) retiré(s).`, 'Retrait par lot');
  }

  /**
   * Keep the candidates that are not already scanned on the WOI.
   * The candidates without equipment are kept: they will be reported as not found if picked.
   */
  public getPendingCandidates(candidates: IBulkScanCandidate[], scannedCodes: string[]): IBulkScanCandidate[] {
    return candidates.filter((candidate: IBulkScanCandidate) => {
      return !candidate.equipment || !scannedCodes.includes(candidate.equipment.code);
    });
  }

  /**
   * Split the picked candidates between the ones that can be added and the ones that must be rejected.
   */
  public validateCandidates(
    candidates: IBulkScanCandidate[],
    options: {
      woi: IWorkOrderItems;
      equipmentModel?: IEquipmentModel;
      requireMacAddress?: boolean;
      requireIpui?: boolean;
    },
  ): { accepted: IEquipment[], rejected: IBulkScanRejection[] } {
    const accepted: IEquipment[] = [];
    const rejected: IBulkScanRejection[] = [];

    candidates.forEach((candidate: IBulkScanCandidate) => {
      const reasons: string[] = this._getRejectionReasons(candidate, options);
      if (reasons.length) {
        rejected.push({ value: candidate.internalSerialNumber, reason: reasons.join(' ') });
      } else {
        accepted.push(candidate.equipment);
      }
    });

    return { accepted, rejected };
  }

  private _getRejectionReasons(
    candidate: IBulkScanCandidate,
    options: {
      woi: IWorkOrderItems;
      equipmentModel?: IEquipmentModel;
      requireMacAddress?: boolean;
      requireIpui?: boolean;
    },
  ): string[] {
    const equipment: IEquipment | undefined = candidate.equipment;
    if (!equipment) {
      return ['Équipement introuvable (SNI inconnu ou équipement inactif).'];
    }

    // The equipment is waiting in the collect area for ANOTHER task: taking it would break that task.
    const reservedByAnotherWoi: boolean = equipment.location === EquipmentLocationEnum.Collect
      && !!equipment.woi_collect
      && equipment.woi_collect !== options.woi.code;
    if (reservedByAnotherWoi) {
      return [`Équipement réservé pour la tâche ${equipment.woi_collect}.`];
    }

    // Reserved for another entity, already assigned to a site, wrong model
    const errors: string[] = this.scannedEquipmentManager.checkForErrors(
      equipment,
      options.woi,
      options.equipmentModel as IEquipmentModel | undefined,
    );

    if (options.requireMacAddress && equipment.model?.need_mac_address && !equipment.mac_address) {
      errors.push('Adresse MAC requise : à renseigner via le scan unitaire.');
    }

    if (options.requireIpui && equipment.model?.need_ipui && !equipment.ipui) {
      errors.push('IPUI requis : à renseigner via le scan unitaire.');
    }

    return errors;
  }

  /**
   * Fetch, in a single request, the active equipments matching the requested SNIs.
   * The result keeps the requested order.
   */
  private async _resolveCandidates(requested: string[]): Promise<IBulkScanCandidate[]> {
    const response: IListResult<IEquipment> = await this.apiShiva.equipments.list({
      internal_serial_number__in: requested.join(','),
      is_active: true,
      limit: requested.length,
    });
    const found: IEquipment[] = response.results || [];

    return requested.map((sni: string): IBulkScanCandidate => ({
      internalSerialNumber: sni,
      equipment: found.find((eqp: IEquipment) => (eqp.internal_serial_number || '').toLowerCase() === sni.toLowerCase()),
    }));
  }

  private _getScannedCodes(woi: IWorkOrderItems, statuses: ScanStatusEnum[]): string[] {
    const scanned: IMetadataScannedEquipment[] = woi.metadata?.equipments || [];
    return scanned
      .filter((item: IMetadataScannedEquipment) => statuses.includes(item.scan_status as ScanStatusEnum))
      .map((item: IMetadataScannedEquipment) => item.code);
  }

  private async _askQuantity(
    options: IBulkScanOptions,
    maxQuantity: number,
    scannedCount: number,
    pendingCount: number,
  ): Promise<number | undefined> {
    const summaryRows: IBulkQuantitySummaryRow[] = [
      { label: 'Quantité demandée', value: options.quantityToScan },
      { label: 'Quantité scannée', value: scannedCount },
      { label: 'SNI demandés restants', value: pendingCount },
    ];

    return this._openQuantityModal({
      title: 'Ajout par lot',
      subtitle: options.woi.code,
      quantityLabel: 'Quantité à ajouter',
      maxQuantity,
      summaryRows,
    });
  }

  private async _openQuantityModal(config: IBulkQuantityModalConfig): Promise<number | undefined> {
    const modal = this.ngbModal.open(BulkQuantityModalComponent, { backdrop: 'static', size: 'md' });

    Object.assign(modal.componentInstance, config);

    try {
      const result: { quantity: number } = await modal.result;
      return result.quantity;
    } catch {
      // The modal was cancelled, nothing to do
      return undefined;
    }
  }

  private _report(acceptedCount: number, rejected: IBulkScanRejection[]): void {
    if (!rejected.length) {
      this.toastr.success(`${acceptedCount} équipement(s) ajouté(s).`, 'Ajout par lot');
      return;
    }
    const modal = this.ngbModal.open(BulkScanResultModalComponent, { size: 'lg' });
    modal.componentInstance.title = 'Résultat de l\'ajout par lot';
    modal.componentInstance.acceptedCount = acceptedCount;
    modal.componentInstance.rejected = rejected;
    modal.componentInstance.valueLabel = 'SNI demandé';
    modal.result.catch(() => undefined);
  }
}
